import React from 'react';
import { Switch, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Unicon from '@/components/General/Unicon';
import { MILESTONES_ENABLED } from '@/config/milestones';
import { setOwnMilestoneCelebrationsEnabled } from '@/data/milestones';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';

/**
 * Account-level "Milestone celebrations" preference (§2.4, §11 — User
 * Settings). Backed by the atomic set_own_milestone_celebrations_enabled
 * RPC — deliberately NOT saveUserSettings (its theme-default behavior can
 * reset unrelated fields).
 *
 * Value reflects the server's own celebration state (celebrationsEnabled),
 * falling back to the mapped userSettings preference while it loads. Turning
 * it off suppresses pending automatic presentations account-wide; earned
 * badges and manual replay are unaffected, and re-enabling does not replay
 * the suppressed backlog (server semantics).
 */
const MilestonePreferences = ({ testID = 'milestone-auto-celebrations-toggle' }) => {
    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const organizationID = loggedInUser?.organizationID;
    const accountID = loggedInUser?.accountID;
    const personID = loggedInUser?.id;

    const stateQuery = Query.milestones.celebrations({ organizationID, accountID, personID });

    const serverValue = stateQuery.data?.celebrationsEnabled;
    const value = serverValue !== undefined && serverValue !== null
        ? serverValue !== false
        : loggedInUser?.userSettings?.autoMilestoneCelebrationsEnabled !== false;

    const mutation = useMutation({
        mutationFn: (enabled) => setOwnMilestoneCelebrationsEnabled(enabled),
        onSuccess: (data, enabled) => {
            const next = data?.celebrationsEnabled ?? enabled;
            // Reflect the preference in the mapped userSettings without a
            // heavy profile refetch — and never touch theme (§12).
            queryClient.setQueryData(QueryKeys.people.loggedInUser(), (old) => old
                ? { ...old, userSettings: { ...(old.userSettings ?? {}), autoMilestoneCelebrationsEnabled: next } }
                : old);
            queryClient.setQueryData(
                QueryKeys.milestones.celebrations(organizationID, accountID, personID),
                (old) => (old ? { ...old, celebrationsEnabled: next, pendingCount: next === false ? 0 : old.pendingCount } : old),
            );
        },
    });

    if (!MILESTONES_ENABLED || !accountID) return null;

    return (
        <View style={styles.row}>
            <View style={styles.iconCircle}>
                <Unicon name="trophy-outline" size={18} colorVariant="gold" />
            </View>
            <View style={styles.copy}>
                <Text style={styles.title}>Milestone celebrations</Text>
                <Text style={styles.body}>Show a small achievement toast when you reach a milestone. Badges still appear on your profile either way.</Text>
            </View>
            <Switch
                testID={testID}
                accessibilityLabel="Milestone celebrations"
                value={!!value}
                disabled={mutation.isPending}
                onValueChange={(enabled) => mutation.mutate(enabled)}
            />
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing }) => ({
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.md,
        paddingHorizontal: spacing.md,
        paddingVertical: 14,
    },
    iconCircle: {
        width: 34,
        height: 34,
        borderRadius: 10,
        backgroundColor: colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    copy: {
        flex: 1,
        gap: 2,
        minWidth: 0,
    },
    title: {
        color: colors.text,
        fontFamily: fonts.bold,
        fontSize: 15,
    },
    body: {
        color: colors.textTertiary,
        fontFamily: fonts.body,
        fontSize: 12,
        lineHeight: 17,
        marginTop: 2,
    },
}));

export default MilestonePreferences;
